import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Copy, MoreVertical, Star, Search, LayoutGrid, List, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, X, DollarSign, ShoppingBag, Percent, ArrowRight, Zap, ExternalLink, Archive, ChevronRight, Calculator } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Pagination } from '../ui/Pagination';
interface ServicesTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
// --- MOCK DATA ---
const initialServices = [{
  id: 1,
  name: 'Разбор натальной карты',
  category: 'Натальная карта',
  price: 5000,
  duration: '60 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 124,
  revenue: 620000,
  conversion: 18.5,
  rating: 4.9,
  returns: 2,
  isProblem: false
}, {
  id: 2,
  name: 'Соляр на год',
  category: 'Прогнозы',
  price: 4000,
  duration: '3 дня',
  format: 'Текстовый (PDF)',
  status: 'active',
  sales: 89,
  revenue: 356000,
  conversion: 12.3,
  rating: 4.7,
  returns: 1,
  isProblem: false
}, {
  id: 3,
  name: 'Синастрия (Совместимость)',
  category: 'Отношения',
  price: 6000,
  duration: '90 мин',
  format: 'Видео-звонок',
  status: 'active',
  sales: 45,
  revenue: 270000,
  conversion: 9.8,
  rating: 4.8,
  returns: 0,
  isProblem: false
}, {
  id: 4,
  name: 'Детский гороскоп',
  category: 'Натальная карта',
  price: 4500,
  duration: '5 дней',
  format: 'Текстовый (PDF)',
  status: 'draft',
  sales: 0,
  revenue: 0,
  conversion: 0,
  rating: 0,
  returns: 0,
  isProblem: true,
  problemText: 'Не заполнено описание'
}, {
  id: 5,
  name: 'Экспресс-ответ на вопрос',
  category: 'Консультации',
  price: 1500,
  duration: '24 часа',
  format: 'Чат',
  status: 'hidden',
  sales: 210,
  revenue: 315000,
  conversion: 22.1,
  rating: 4.6,
  returns: 5,
  isProblem: true,
  problemText: 'Высокий процент возвратов'
}];
export function ServicesTab({
  showToast,
  setActiveTab
}: ServicesTabProps) {
  // --- STATE ---
  const [services, setServices] = useState(initialServices);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modals state
  const [creatingService, setCreatingService] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // --- COMPUTED DATA ---
  const filteredServices = useMemo(() => {
    return services.filter(s => {
      const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [services, searchQuery, filterStatus]);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = filteredServices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalRevenue = services.reduce((sum, s) => sum + s.revenue, 0);
  const totalSales = services.reduce((sum, s) => sum + s.sales, 0);
  const avgCheck = totalSales > 0 ? Math.round(totalRevenue / totalSales) : 0;
  const bestService = [...services].sort((a, b) => b.revenue - a.revenue)[0];
  const worstService = [...services].filter(s => s.status === 'active').sort((a, b) => a.revenue - b.revenue)[0];
  const problemServices = services.filter(s => s.isProblem);
  // --- HANDLERS ---
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredServices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredServices.map(s => s.id)));
    }
  };
  const toggleSelect = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleStatusToggle = (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setServices(services.map(s => {
      if (s.id === id) {
        const newStatus = s.status === 'active' ? 'hidden' : 'active';
        showToast(`Услуга "${s.name}" ${newStatus === 'active' ? 'опубликована' : 'скрыта'}`);
        return {
          ...s,
          status: newStatus
        };
      }
      return s;
    }));
    setActiveDropdown(null);
  };
  const handleDelete = (id: number) => {
    setServices(services.filter(s => s.id !== id));
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setDeleteConfirmId(null);
    showToast('Услуга удалена');
  };
  const handleDuplicate = (service: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const newService = {
      ...service,
      id: Date.now(),
      name: `${service.name} (Копия)`,
      status: 'draft',
      sales: 0,
      revenue: 0,
      returns: 0,
      isProblem: false
    };
    setServices([...services, newService]);
    showToast('Услуга дублирована');
    setActiveDropdown(null);
  };
  const handleBulkAction = (action: 'hide' | 'activate' | 'delete') => {
    if (action === 'delete') {
      setServices(services.filter(s => !selectedIds.has(s.id)));
      showToast(`Удалено услуг: ${selectedIds.size}`);
    } else {
      const newStatus = action === 'activate' ? 'active' : 'hidden';
      setServices(services.map(s => selectedIds.has(s.id) ? {
        ...s,
        status: newStatus
      } : s));
      showToast(`Статус изменен для ${selectedIds.size} услуг`);
    }
    setSelectedIds(new Set());
  };
  const navigateToOrders = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к заказам: ${serviceName}`);
    if (setActiveTab) setActiveTab('orders');
  };
  const navigateToPromotion = (serviceName: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    showToast(`Переход к продвижению: ${serviceName}`);
    if (setActiveTab) setActiveTab('promotion');
  };
  // --- RENDERERS ---
  // 1. CREATE WIZARD MODAL
  const renderCreateWizard = () => {
    if (!creatingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-15133">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-15134">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15135">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-15136">
              Создание новой услуги
            </h3>
            <button onClick={() => setCreatingService(false)} className="text-slate-400 hover:text-slate-600" data-id="element-15137">
              <X className="w-6 h-6" data-id="element-15138" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-6" data-id="element-15139">
            <div className="space-y-4" data-id="element-15140">
              <div data-id="element-15141">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15142">
                  Название услуги
                </label>
                <Input placeholder="Например: Разбор натальной карты" fullWidth data-id="element-15143" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-15144">
                <div data-id="element-15145">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15146">
                    Категория
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-15147">
                    <option data-id="element-15148">Натальная карта</option>
                    <option data-id="element-15149">Прогнозы</option>
                    <option data-id="element-15150">Отношения</option>
                    <option data-id="element-15151">Консультации</option>
                  </select>
                </div>
                <div data-id="element-15152">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15153">
                    Формат
                  </label>
                  <select className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" data-id="element-15154">
                    <option data-id="element-15155">Видео-звонок</option>
                    <option data-id="element-15156">Текстовый (PDF)</option>
                    <option data-id="element-15157">Аудио-сообщение</option>
                    <option data-id="element-15158">Чат</option>
                  </select>
                </div>
              </div>

              {/* Calculator Block */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mt-6" data-id="element-15159">
                <h4 className="text-sm font-bold text-indigo-900 flex items-center gap-2 mb-3" data-id="element-15160">
                  <Calculator className="w-4 h-4" data-id="element-15161" /> Калькулятор дохода
                </h4>
                <div className="space-y-3" data-id="element-15162">
                  <div data-id="element-15163">
                    <label className="block text-xs font-medium text-indigo-700 mb-1" data-id="element-15164">
                      Цена для клиента (₽)
                    </label>
                    <Input type="number" defaultValue="5000" fullWidth className="bg-white" data-id="element-15165" />
                  </div>
                  <div className="flex justify-between items-center text-sm py-2 border-b border-indigo-200/50" data-id="element-15166">
                    <span className="text-indigo-600" data-id="element-15167">
                      Комиссия платформы (20%)
                    </span>
                    <span className="font-medium text-rose-600" data-id="element-15168">- 1 000 ₽</span>
                  </div>
                  <div className="flex justify-between items-center text-base font-bold pt-1" data-id="element-15169">
                    <span className="text-indigo-900" data-id="element-15170">Ваш чистый доход</span>
                    <span className="text-emerald-600" data-id="element-15171">4 000 ₽</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-end gap-3 z-10" data-id="element-15172">
            <Button variant="outline" onClick={() => setCreatingService(false)} data-id="element-15173">
              Отмена
            </Button>
            <Button variant="primary" onClick={() => {
            showToast('Услуга успешно создана и сохранена в черновики');
            setCreatingService(false);
          }} data-id="element-15174">
              Сохранить черновик
            </Button>
            <Button variant="primary" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
            showToast('Услуга успешно опубликована');
            setCreatingService(false);
          }} data-id="element-15175">
              Опубликовать
            </Button>
          </div>
        </div>
      </div>;
  };
  // 2. EDIT MODAL
  const renderEditModal = () => {
    if (!editingService) return null;
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-15176">
        <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200" data-id="element-15177">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15178">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-15179">
              Редактирование: {editingService.name}
            </h3>
            <button onClick={() => setEditingService(null)} className="text-slate-400 hover:text-slate-600" data-id="element-15180">
              <X className="w-6 h-6" data-id="element-15181" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-4" data-id="element-15182">
            <div data-id="element-15183">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15184">
                Название услуги
              </label>
              <Input defaultValue={editingService.name} fullWidth data-id="element-15185" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-15186">
              <div data-id="element-15187">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15188">
                  Цена (₽)
                </label>
                <Input type="number" defaultValue={editingService.price} fullWidth data-id="element-15189" />
              </div>
              <div data-id="element-15190">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-15191">
                  Длительность
                </label>
                <Input defaultValue={editingService.duration} fullWidth data-id="element-15192" />
              </div>
            </div>
            {editingService.isProblem && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3 mt-4" data-id="element-15193">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-15194" />
                <div data-id="element-15195">
                  <h4 className="text-sm font-bold text-rose-900" data-id="element-15196">
                    Проблема: {editingService.problemText}
                  </h4>
                  <p className="text-xs text-rose-700 mt-1" data-id="element-15197">
                    Исправьте проблему, чтобы увеличить конверсию.
                  </p>
                </div>
              </div>}
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 sm:p-6 flex justify-between items-center z-10" data-id="element-15198">
            <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => {
            setDeleteConfirmId(editingService.id);
            setEditingService(null);
          }} data-id="element-15199">
              Удалить
            </Button>
            <div className="flex gap-3" data-id="element-15200">
              <Button variant="outline" onClick={() => setEditingService(null)} data-id="element-15201">
                Отмена
              </Button>
              <Button variant="primary" onClick={() => {
              showToast('Изменения сохранены');
              setEditingService(null);
            }} data-id="element-15202">
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      </div>;
  };
  // 3. DELETE CONFIRM MODAL
  const renderDeleteConfirm = () => {
    if (!deleteConfirmId) return null;
    const service = services.find(s => s.id === deleteConfirmId);
    return <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-15203">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-in zoom-in-95 duration-200 text-center" data-id="element-15204">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-15205">
            <Trash2 className="w-8 h-8 text-rose-600" data-id="element-15206" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-15207">
            Удалить услугу?
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-15208">
            Вы уверены, что хотите удалить "{service?.name}"? Это действие
            нельзя отменить.
          </p>
          <div className="flex gap-3" data-id="element-15209">
            <Button variant="outline" fullWidth onClick={() => setDeleteConfirmId(null)} data-id="element-15210">
              Отмена
            </Button>
            <Button variant="primary" fullWidth className="bg-rose-600 hover:bg-rose-700" onClick={() => handleDelete(deleteConfirmId)} data-id="element-15211">
              Удалить
            </Button>
          </div>
        </div>
      </div>;
  };
  // --- MAIN RENDER ---
  return <div className="space-y-6 animate-in fade-in duration-300 pb-20" data-id="element-15212">
      {renderCreateWizard()}
      {renderEditModal()}
      {renderDeleteConfirm()}

      {/* 1. HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15213">
        <div data-id="element-15214">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-15215">Услуги</h2>
          <p className="text-slate-500 text-sm" data-id="element-15216">
            Управление вашими консультациями и продуктами
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-15218" />} onClick={() => setCreatingService(true)} data-id="element-15217">
          Создать услугу
        </Button>
      </div>

      {/* 2. KPI BLOCK */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-id="element-15219">
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-15220">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15221">
            Общая выручка
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15222">
            {totalRevenue.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-15223">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-15224" /> +12% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => setFilterStatus('all')} data-id="element-15225">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15226">
            Продажи
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15227">
            {totalSales}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 flex items-center" data-id="element-15228">
            <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-15229" /> +5% за месяц
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-indigo-300 cursor-pointer transition-colors" data-id="element-15230">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15231">
            Средний чек
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1" data-id="element-15232">
            {avgCheck.toLocaleString('ru-RU')} ₽
          </div>
          <div className="text-[10px] font-bold text-slate-500 flex items-center" data-id="element-15233">
            По всем услугам
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-emerald-300 cursor-pointer transition-colors bg-emerald-50/30" onClick={() => {
        setSearchQuery(bestService?.name || '');
      }} data-id="element-15234">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15235">
            Лучшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={bestService?.name} data-id="element-15236">
            {bestService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-emerald-600 mt-1" data-id="element-15237">
            {bestService?.sales} продаж
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200 hover:border-rose-300 cursor-pointer transition-colors bg-rose-50/30" onClick={() => {
        setSearchQuery(worstService?.name || '');
      }} data-id="element-15238">
          <div className="text-xs text-slate-500 font-medium mb-1 uppercase tracking-wider" data-id="element-15239">
            Худшая услуга
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" title={worstService?.name} data-id="element-15240">
            {worstService?.name || '-'}
          </div>
          <div className="text-[10px] font-bold text-rose-600 mt-1" data-id="element-15241">
            Нужно улучшить
          </div>
        </Card>
      </div>

      {/* 3. PROBLEM SERVICES ALERT */}
      {problemServices.length > 0 && <Card padding="md" className="border-rose-200 bg-rose-50/50" data-id="element-15242">
          <div className="flex items-center gap-2 mb-3" data-id="element-15243">
            <AlertCircle className="w-5 h-5 text-rose-600" data-id="element-15244" />
            <h3 className="text-base font-bold text-rose-900" data-id="element-15245">
              Проблемные услуги ({problemServices.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-15246">
            {problemServices.map(s => <div key={`prob-${s.id}`} className="bg-white p-3 rounded-xl border border-rose-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 shadow-sm cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setEditingService(s)} data-id="element-15247">
                <div data-id="element-15248">
                  <div className="text-sm font-bold text-slate-900" data-id="element-15249">
                    {s.name}
                  </div>
                  <div className="text-xs text-rose-600 mt-0.5" data-id="element-15250">
                    {s.problemText}
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto" data-id="element-15251">
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none text-rose-600 border-rose-200 hover:bg-rose-50" onClick={e => {
              e.stopPropagation();
              setEditingService(s);
            }} data-id="element-15252">
                    Исправить
                  </Button>
                  <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15253">
                    Реклама
                  </Button>
                </div>
              </div>)}
          </div>
        </Card>}

      {/* 4. REVENUE GROWTH RECOMMENDATIONS */}
      <Card padding="md" className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50" data-id="element-15254">
        <div className="flex items-center gap-2 mb-3" data-id="element-15255">
          <Zap className="w-5 h-5 text-indigo-600" data-id="element-15256" />
          <h3 className="text-base font-bold text-indigo-900" data-id="element-15257">
            Точки роста дохода
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-15258">
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-15259">
            <div data-id="element-15260">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-15261">
                Поднимите цену на "Разбор натальной карты"
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-15262">
                Конверсия 18.5% — это очень высоко. Вы можете безопасно поднять
                цену на 10-15%.
              </p>
            </div>
            <Button variant="outline" size="sm" className="w-full text-indigo-700 border-indigo-200" onClick={() => {
            const s = services.find(s => s.id === 1);
            if (s) setEditingService(s);
          }} data-id="element-15263">
              Применить
            </Button>
          </div>
          <div className="bg-white p-3 rounded-xl border border-indigo-100 shadow-sm flex flex-col justify-between" data-id="element-15264">
            <div data-id="element-15265">
              <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-15266">
                Создайте пакетную услугу
              </div>
              <p className="text-xs text-slate-600 mb-3" data-id="element-15267">
                Клиенты часто покупают Соляр после Натальной карты. Объедините
                их со скидкой 10%.
              </p>
            </div>
            <Button variant="primary" size="sm" className="w-full bg-indigo-600" onClick={() => setCreatingService(true)} data-id="element-15268">
              Создать пакет
            </Button>
          </div>
        </div>
      </Card>

      {/* 5. SEARCH, FILTERS & VIEW TOGGLE */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-15269">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-15270">
          <div className="relative w-full sm:w-64" data-id="element-15271">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-15272" />
            <input type="text" placeholder="Поиск услуг..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-15273" />
          </div>
          <Button variant={showFilters ? 'primary' : 'outline'} icon={<Search className="w-4 h-4" data-id="element-15275" />} onClick={() => setShowFilters(!showFilters)} className="w-full sm:w-auto" data-id="element-15274">
            Фильтры
          </Button>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-lg w-full sm:w-auto" data-id="element-15276">
          <button onClick={() => setViewMode('table')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'table' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-15277">
            <List className="w-4 h-4" data-id="element-15278" />{' '}
            <span className="hidden sm:inline" data-id="element-15279">Таблица</span>
          </button>
          <button onClick={() => setViewMode('cards')} className={`flex-1 sm:flex-none px-3 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${viewMode === 'cards' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-15280">
            <LayoutGrid className="w-4 h-4" data-id="element-15281" />{' '}
            <span className="hidden sm:inline" data-id="element-15282">Карточки</span>
          </button>
        </div>
      </div>

      {/* FILTER PANEL */}
      {showFilters && <Card padding="sm" className="border-slate-200 bg-slate-50 animate-in slide-in-from-top-2 duration-200" data-id="element-15283">
          <div className="flex flex-wrap gap-2" data-id="element-15284">
            <button onClick={() => setFilterStatus('all')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-15285">
              Все статусы
            </button>
            <button onClick={() => setFilterStatus('active')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'active' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-300'}`} data-id="element-15286">
              Активные
            </button>
            <button onClick={() => setFilterStatus('hidden')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'hidden' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:border-amber-300'}`} data-id="element-15287">
              Скрытые
            </button>
            <button onClick={() => setFilterStatus('draft')} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterStatus === 'draft' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-15288">
              Черновики
            </button>
          </div>
        </Card>}

      {/* BULK ACTIONS BAR */}
      {selectedIds.size > 0 && <div className="bg-indigo-600 text-white p-3 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-4 animate-in slide-in-from-bottom-4 shadow-lg sticky top-4 z-40" data-id="element-15289">
          <div className="text-sm font-medium" data-id="element-15290">
            Выбрано услуг: {selectedIds.size}
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-15291">
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('activate')} data-id="element-15292">
              <Eye className="w-4 h-4 mr-1.5" data-id="element-15293" /> Активировать
            </Button>
            <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex-1 sm:flex-none" onClick={() => handleBulkAction('hide')} data-id="element-15294">
              <EyeOff className="w-4 h-4 mr-1.5" data-id="element-15295" /> Скрыть
            </Button>
            <Button variant="outline" size="sm" className="bg-rose-500/20 border-rose-500/30 text-rose-100 hover:bg-rose-500/40 flex-1 sm:flex-none" onClick={() => handleBulkAction('delete')} data-id="element-15296">
              <Trash2 className="w-4 h-4 mr-1.5" data-id="element-15297" /> Удалить
            </Button>
          </div>
        </div>}

      {/* 6. MAIN CONTENT (TABLE OR CARDS) */}
      {filteredServices.length === 0 ? <Card padding="lg" className="text-center border-dashed border-2 border-slate-200" data-id="element-15298">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-15299">
            <Search className="w-8 h-8 text-slate-300" data-id="element-15300" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-15301">
            Услуги не найдены
          </h3>
          <p className="text-slate-500 mb-6" data-id="element-15302">
            Попробуйте изменить параметры поиска или фильтры.
          </p>
          <Button variant="primary" onClick={() => {
        setSearchQuery('');
        setFilterStatus('all');
      }} data-id="element-15303">
            Сбросить фильтры
          </Button>
        </Card> : viewMode === 'table' /* TABLE VIEW */ ? <Card padding="none" className="border-slate-200 overflow-visible" data-id="element-15304">
          <div className="overflow-x-auto" data-id="element-15305">
            <table className="w-full text-left whitespace-nowrap" data-id="element-15306">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-15307">
                <tr data-id="element-15308">
                  <th className="px-4 py-3 w-10 text-center" data-id="element-15309">
                    <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === filteredServices.length && filteredServices.length > 0} onChange={toggleSelectAll} data-id="element-15310" />
                  </th>
                  <th className="px-4 py-3 font-medium" data-id="element-15311">Название</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15312">Цена</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15313">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15314">Продажи / Выручка</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15315">Конверсия</th>
                  <th className="px-4 py-3 font-medium" data-id="element-15316">Рейтинг</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-15317">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-15318">
                {paginatedServices.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.has(s.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-15319">
                    <td className="px-4 py-3 text-center" data-id="element-15320">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-15321" />
                    </td>
                    <td className="px-4 py-3" data-id="element-15322">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-2" onClick={() => setEditingService(s)} data-id="element-15323">
                        {s.name}
                        {s.isProblem && <AlertCircle className="w-3.5 h-3.5 text-rose-500" title={s.problemText} data-id="element-15324" />}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-15325">
                        {s.category} • {s.format}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-15326">
                      {s.price.toLocaleString('ru-RU')} ₽
                    </td>
                    <td className="px-4 py-3" data-id="element-15327">
                      <div className="cursor-pointer inline-block" onClick={e => handleStatusToggle(s.id, e)} title="Нажмите для переключения" data-id="element-15328">
                        {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-15329">
                            Активна
                          </Badge>}
                        {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-15330">
                            Черновик
                          </Badge>}
                        {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-15331">
                            Скрыта
                          </Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15332">
                      <div className="text-sm font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-1" onClick={e => navigateToOrders(s.name, e)} data-id="element-15333">
                        {s.sales} шт. <ChevronRight className="w-3 h-3" data-id="element-15334" />
                      </div>
                      <div className="text-xs font-medium text-emerald-600" data-id="element-15335">
                        {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15336">
                      <div className="text-sm text-slate-700" data-id="element-15337">
                        {s.conversion > 0 ? `${s.conversion}%` : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-15338">
                      {s.rating > 0 ? <div className="flex items-center gap-1 text-sm font-medium text-slate-700" data-id="element-15339">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-15340" />{' '}
                          {s.rating}
                        </div> : <span className="text-slate-400 text-sm" data-id="element-15341">-</span>}
                      {s.returns > 0 && <div className="text-[10px] text-rose-500 mt-0.5" data-id="element-15342">
                          {s.returns} возвратов
                        </div>}
                    </td>
                    <td className="px-4 py-3 text-right relative" data-id="element-15343">
                      <div className="flex items-center justify-end gap-1" data-id="element-15344">
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => setEditingService(s)} data-id="element-15345">
                          <Edit2 className="w-4 h-4" data-id="element-15346" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`w-8 h-8 p-0 ${s.status === 'active' ? 'text-slate-400 hover:text-amber-600' : 'text-slate-400 hover:text-emerald-600'}`} title={s.status === 'active' ? 'Скрыть' : 'Опубликовать'} onClick={e => handleStatusToggle(s.id, e)} data-id="element-15347">
                          {s.status === 'active' ? <EyeOff className="w-4 h-4" data-id="element-15348" /> : <Eye className="w-4 h-4" data-id="element-15349" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-slate-900" onClick={e => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === s.id ? null : s.id);
                  }} data-id="element-15350">
                          <MoreVertical className="w-4 h-4" data-id="element-15351" />
                        </Button>
                      </div>

                      {/* Dropdown Menu */}
                      {activeDropdown === s.id && <div className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-15352">
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-15353">
                            <Copy className="w-4 h-4 text-slate-400" data-id="element-15354" />{' '}
                            Дублировать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15355">
                            <Zap className="w-4 h-4 text-amber-500" data-id="element-15356" />{' '}
                            Продвигать
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Публичная страница открыта');
                    setActiveDropdown(null);
                  }} data-id="element-15357">
                            <ExternalLink className="w-4 h-4 text-slate-400" data-id="element-15358" />{' '}
                            Открыть страницу
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-15359"></div>
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    showToast('Услуга в архиве');
                    setActiveDropdown(null);
                  }} data-id="element-15360">
                            <Archive className="w-4 h-4 text-slate-400" data-id="element-15361" /> В
                            архив
                          </button>
                          <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
                    e.stopPropagation();
                    setDeleteConfirmId(s.id);
                    setActiveDropdown(null);
                  }} data-id="element-15362">
                            <Trash2 className="w-4 h-4 text-rose-500" data-id="element-15363" /> Удалить
                          </button>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card> /* CARDS VIEW (Mobile friendly) */ : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-15364">
          {paginatedServices.map(s => <Card key={s.id} padding="md" className={`border-slate-200 relative ${selectedIds.has(s.id) ? 'ring-2 ring-indigo-500' : ''}`} data-id="element-15365">
              <div className="absolute top-4 right-4 flex gap-2" data-id="element-15366">
                <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-5 h-5" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} data-id="element-15367" />
              </div>

              <div className="mb-4 pr-8" data-id="element-15368">
                <div className="flex items-center gap-2 mb-2" data-id="element-15369">
                  {s.status === 'active' && <Badge variant="success" size="sm" data-id="element-15370">
                      Активна
                    </Badge>}
                  {s.status === 'draft' && <Badge variant="neutral" size="sm" data-id="element-15371">
                      Черновик
                    </Badge>}
                  {s.status === 'hidden' && <Badge variant="warning" size="sm" data-id="element-15372">
                      Скрыта
                    </Badge>}
                  {s.isProblem && <AlertCircle className="w-4 h-4 text-rose-500" title={s.problemText} data-id="element-15373" />}
                </div>
                <h3 className="text-lg font-bold text-slate-900 cursor-pointer hover:text-indigo-600" onClick={() => setEditingService(s)} data-id="element-15374">
                  {s.name}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-15375">
                  {s.category} • {s.format}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 bg-slate-50 p-3 rounded-xl" data-id="element-15376">
                <div data-id="element-15377">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15378">
                    Цена
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-15379">
                    {s.price.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="cursor-pointer hover:bg-slate-200/50 rounded p-1 -m-1 transition-colors" onClick={e => navigateToOrders(s.name, e)} data-id="element-15380">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15381">
                    Продажи
                  </div>
                  <div className="font-bold text-indigo-600 flex items-center" data-id="element-15382">
                    {s.sales} <ChevronRight className="w-3 h-3 ml-0.5" data-id="element-15383" />
                  </div>
                </div>
                <div data-id="element-15384">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15385">
                    Выручка
                  </div>
                  <div className="font-bold text-emerald-600" data-id="element-15386">
                    {s.revenue > 0 ? `${s.revenue.toLocaleString('ru-RU')} ₽` : '-'}
                  </div>
                </div>
                <div data-id="element-15387">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1" data-id="element-15388">
                    Конверсия
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-15389">
                    {s.conversion > 0 ? `${s.conversion}%` : '-'}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-15390">
                <div className="flex gap-1" data-id="element-15391">
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={() => setEditingService(s)} data-id="element-15392">
                    <Edit2 className="w-4 h-4 text-slate-600" data-id="element-15393" />
                  </Button>
                  <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={e => handleStatusToggle(s.id, e)} data-id="element-15394">
                    {s.status === 'active' ? <EyeOff className="w-4 h-4 text-amber-600" data-id="element-15395" /> : <Eye className="w-4 h-4 text-emerald-600" data-id="element-15396" />}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="text-slate-500" onClick={e => {
            e.stopPropagation();
            setActiveDropdown(activeDropdown === s.id ? null : s.id);
          }} data-id="element-15397">
                  Еще <MoreVertical className="w-4 h-4 ml-1" data-id="element-15398" />
                </Button>
              </div>

              {/* Mobile Dropdown Menu */}
              {activeDropdown === s.id && <div className="absolute right-4 bottom-14 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()} data-id="element-15399">
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => handleDuplicate(s, e)} data-id="element-15400">
                    <Copy className="w-4 h-4 text-slate-400" data-id="element-15401" /> Дублировать
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={e => navigateToPromotion(s.name, e)} data-id="element-15402">
                    <Zap className="w-4 h-4 text-amber-500" data-id="element-15403" /> Продвигать
                  </button>
                  <div className="h-px bg-slate-100 my-1" data-id="element-15404"></div>
                  <button className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={e => {
            e.stopPropagation();
            setDeleteConfirmId(s.id);
            setActiveDropdown(null);
          }} data-id="element-15405">
                    <Trash2 className="w-4 h-4 text-rose-500" data-id="element-15406" /> Удалить
                  </button>
                </div>}
            </Card>)}
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2" data-id="element-15407">
          <span className="text-xs text-slate-500" data-id="element-15408">
            Показано {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(currentPage * ITEMS_PER_PAGE, filteredServices.length)} из{' '}
            {filteredServices.length}
          </span>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-15409" />
        </div>}
    </div>;
}